import { useCallback, useMemo } from "react";

import { useGlobalData } from "@/providers";
import formatCurrency, {
  CURRENCIES_EXCHANGE_RATE_DECIMALS,
  DecimalContext,
  DIGITAL_ASSETS_DISPLAY_DECIMALS,
  DIGITAL_ASSETS_EXCHANGE_RATE_DECIMALS,
  USD_STABLECOINS_REGEX,
} from "@/utils/formatCurrency";

const useCurrencyFormatter = () => {
  const { products } = useGlobalData();

  /**
   * Determines the appropriate number of decimal places for a currency
   * based on the context in which it's being displayed
   */
  const getCurrencyDecimals = useCallback(
    (
      currency: string,
      context: DecimalContext = "display",
      customDisplayDecimals?: number
    ): number => {
      const product = products?.find((p) => p.code === currency);
      const isDigitalAsset =
        product?.decimalPlaces && product?.decimalPlaces > 2;
      const isUsdStablecoin = USD_STABLECOINS_REGEX.test(currency);

      // Default to 2 decimal places if product not found
      if (!product) {
        return 2;
      }

      switch (context) {
        case "exchange_rate":
          // Exchange rate display: high precision for rates
          if (isDigitalAsset && !isUsdStablecoin) {
            return DIGITAL_ASSETS_EXCHANGE_RATE_DECIMALS; // 8 decimals for digital assets
          }
          return CURRENCIES_EXCHANGE_RATE_DECIMALS; // 6 decimals for currencies and stablecoins

        case "input":
          // Input fields: use product precision but max out at 8 for digital assets
          if (isDigitalAsset) {
            return Math.min(
              product.decimalPlaces,
              DIGITAL_ASSETS_EXCHANGE_RATE_DECIMALS
            );
          }
          return product.decimalPlaces;

        case "display":
        default:
          // Standard display formatting
          if (isUsdStablecoin) {
            return 2; // USD stablecoins always show 2 decimals in normal display
          } else if (isDigitalAsset) {
            return customDisplayDecimals ?? DIGITAL_ASSETS_DISPLAY_DECIMALS; // Default 5 for digital assets
          } else {
            return product.decimalPlaces; // Use product's decimal places for currencies
          }
      }
    },
    [products]
  );

  const formatter = useCallback(
    (
      amount: number,
      currency: string,
      numberOnly = false,
      cryptoDisplayDecimals = DIGITAL_ASSETS_DISPLAY_DECIMALS,
      exchangeRate = false
    ): string | undefined => {
      return formatCurrency(
        amount || 0,
        currency,
        products ?? [],
        numberOnly,
        cryptoDisplayDecimals,
        exchangeRate
      );
    },
    [products]
  );

  // Memoize the return so callers that spread the whole object (or use it as
  // a dep) don't re-render every time. Individual functions are already stable
  // via useCallback, so this memo only changes when `products` changes.
  return useMemo(
    () => ({ formatCurrency: formatter, getCurrencyDecimals }),
    [formatter, getCurrencyDecimals]
  );
};

export default useCurrencyFormatter;
